(function () {
  function parseBool(value) {
    return value === true || value === 'true';
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, ms);
    };
  }

  /** Mobile stack layout: offset pagination from slide bottom by active text block height */
  function updateStackFooterHeight(root) {
    if (!root || !parseBool(root.dataset.stackTextBelow)) {
      if (root) root.style.removeProperty('--if-hero-stack-footer');
      return;
    }
    if (typeof window.matchMedia === 'undefined' || window.matchMedia('(min-width: 750px)').matches) {
      root.style.removeProperty('--if-hero-stack-footer');
      return;
    }
    var active = root.querySelector('.swiper-slide-active .if-hero-slide__content-wrap');
    var h = active ? active.offsetHeight : 0;
    root.style.setProperty('--if-hero-stack-footer', Math.max(h, 0) + 'px');
  }

  /** Apply stack footer from ResizeObserver entry (avoids synchronous offsetHeight after layout). */
  function applyStackFooterFromResizeEntry(root, entry) {
    if (!root || !entry) return;
    if (typeof window.matchMedia === 'undefined' || window.matchMedia('(min-width: 750px)').matches) {
      root.style.removeProperty('--if-hero-stack-footer');
      return;
    }
    var h = 0;
    if (entry.borderBoxSize && entry.borderBoxSize.length) {
      h = entry.borderBoxSize[0].blockSize;
    } else {
      h = entry.contentRect.height;
    }
    root.style.setProperty('--if-hero-stack-footer', Math.max(Math.round(h), 0) + 'px');
  }

  function scheduleSwiperUpdate(swiper) {
    if (!swiper || typeof swiper.update !== 'function') return;
    requestAnimationFrame(function () {
      swiper.update();
    });
  }

  /**
   * Defer measure + Swiper.update across frames so reads run after Swiper/layout settle.
   * Immediate swiper.update() after style writes forces sync layout (PageSpeed "forced reflow").
   */
  function scheduleStackMeasure(root, swiper) {
    if (!root || !parseBool(root.dataset.stackTextBelow)) {
      if (root) root.style.removeProperty('--if-hero-stack-footer');
      return;
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        updateStackFooterHeight(root);
        scheduleSwiperUpdate(swiper);
      });
    });
  }

  function bindStackResizeObserver(root, swiper) {
    if (!root || !parseBool(root.dataset.stackTextBelow) || typeof ResizeObserver === 'undefined') {
      return;
    }

    if (root._ifHeroStackRO) {
      root._ifHeroStackRO.disconnect();
    }

    var ro = new ResizeObserver(function (entries) {
      if (!entries.length) return;
      applyStackFooterFromResizeEntry(root, entries[0]);
      scheduleSwiperUpdate(swiper);
    });
    root._ifHeroStackRO = ro;

    function observeActiveContent() {
      ro.disconnect();
      if (typeof window.matchMedia !== 'undefined' && window.matchMedia('(min-width: 750px)').matches) {
        root.style.removeProperty('--if-hero-stack-footer');
        return;
      }
      var active = root.querySelector('.swiper-slide-active .if-hero-slide__content-wrap');
      if (active) {
        ro.observe(active);
      } else {
        root.style.removeProperty('--if-hero-stack-footer');
      }
    }
    root._ifHeroStackObserveActive = observeActiveContent;
    observeActiveContent();
  }

  function getAllSlideVideos(slide) {
    if (!slide) return [];
    return Array.prototype.slice.call(slide.querySelectorAll('.if-hero-slide__video-wrap video'));
  }

  /** Videos inside wraps that are currently displayed (avoids playing two sources when desktop + mobile files exist). */
  function getVisibleSlideVideos(slide) {
    if (!slide) return [];
    if (typeof window.getComputedStyle === 'undefined') {
      return getAllSlideVideos(slide);
    }
    var out = [];
    slide.querySelectorAll('.if-hero-slide__video-wrap').forEach(function (wrap) {
      if (window.getComputedStyle(wrap).display === 'none') return;
      Array.prototype.forEach.call(wrap.querySelectorAll('video'), function (v) {
        out.push(v);
      });
    });
    if (out.length === 0) return getAllSlideVideos(slide);
    return out;
  }

  function syncVideoControlUi(slide) {
    var controls = slide && slide.querySelector('[data-if-hero-video-controls]');
    if (!controls) return;
    var visible = getVisibleSlideVideos(slide);
    var all = getAllSlideVideos(slide);
    if (!all.length) return;
    var playBtn = controls.querySelector('[data-if-hero-video-play]');
    var muteBtn = controls.querySelector('[data-if-hero-video-mute]');
    var anyPlaying =
      visible.length &&
      visible.some(function (v) {
        return !v.paused;
      });
    var allMuted = all.every(function (v) {
      return v.muted;
    });
    if (playBtn) {
      playBtn.classList.toggle('is-playing', !!anyPlaying);
      playBtn.setAttribute('aria-pressed', anyPlaying ? 'true' : 'false');
      playBtn.setAttribute('aria-label', anyPlaying ? 'Pause video' : 'Play video');
    }
    if (muteBtn) {
      muteBtn.classList.toggle('is-muted', allMuted);
      muteBtn.setAttribute('aria-pressed', allMuted ? 'true' : 'false');
      muteBtn.setAttribute('aria-label', allMuted ? 'Unmute video' : 'Mute video');
    }
  }

  function onVideoControlsClick(root, event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var playBtn = t.closest('[data-if-hero-video-play]');
    var muteBtn = t.closest('[data-if-hero-video-mute]');
    if (!playBtn && !muteBtn) return;
    var slide = t.closest('.swiper-slide');
    if (!slide || !root.contains(slide)) return;
    var allVideos = getAllSlideVideos(slide);
    if (!allVideos.length) return;
    event.preventDefault();
    if (playBtn) {
      var visible = getVisibleSlideVideos(slide);
      if (!visible.length) return;
      var anyPlaying = visible.some(function (v) {
        return !v.paused;
      });
      if (anyPlaying) {
        visible.forEach(function (v) {
          v.pause();
        });
      } else {
        visible.forEach(function (v) {
          v.play().catch(function () {});
        });
      }
    }
    if (muteBtn) {
      var nextMuted = !allVideos[0].muted;
      allVideos.forEach(function (v) {
        v.muted = nextMuted;
      });
    }
    syncVideoControlUi(slide);
  }

  function toggleVideos(root, activeSlide) {
    if (!root) return;
    root.querySelectorAll('.if-hero-slide__video-wrap video').forEach(function (video) {
      video.pause();
    });
    if (!activeSlide) return;
    var toPlay = getVisibleSlideVideos(activeSlide);
    toPlay.forEach(function (video) {
      video.play().catch(function () {});
    });
    syncVideoControlUi(activeSlide);
  }

  function isInvalidHeroHref(href) {
    if (!href || typeof href !== 'string') return true;
    var trimmed = href.trim();
    return trimmed === '' || trimmed === '#';
  }

  function navigateHeroLink(link) {
    if (!link || isInvalidHeroHref(link.getAttribute('href'))) return;
    if (link.target === '_blank') {
      window.open(link.href, '_blank', 'noopener');
      return;
    }
    window.location.assign(link.href);
  }

  function unbindClickableHeroSlides(root) {
    if (!root) return;
    if (root._ifHeroClickableClick) {
      root.removeEventListener('click', root._ifHeroClickableClick, true);
      root._ifHeroClickableClick = null;
    }
    if (root._ifHeroClickablePointerUp) {
      root.removeEventListener('pointerup', root._ifHeroClickablePointerUp);
      root._ifHeroClickablePointerUp = null;
    }
    if (root._ifHeroClickablePointerDown) {
      root.removeEventListener('pointerdown', root._ifHeroClickablePointerDown);
      root._ifHeroClickablePointerDown = null;
    }
    if (root._ifHeroClickablePointerMove) {
      root.removeEventListener('pointermove', root._ifHeroClickablePointerMove);
      root._ifHeroClickablePointerMove = null;
    }
  }

  function bindClickableHeroSlides(root) {
    if (!root) return;

    unbindClickableHeroSlides(root);

    if (!root.querySelector('.if-hero-slide__frame--clickable')) return;

    var pointerState = { active: false, moved: false, startX: 0, startY: 0 };

    var onPointerDown = function (event) {
      if (!event.target.closest('.if-hero-slide__frame--clickable')) return;
      pointerState.active = true;
      pointerState.moved = false;
      pointerState.startX = event.clientX;
      pointerState.startY = event.clientY;
    };

    var onPointerMove = function (event) {
      if (!pointerState.active) return;
      if (
        Math.abs(event.clientX - pointerState.startX) > 10 ||
        Math.abs(event.clientY - pointerState.startY) > 10
      ) {
        pointerState.moved = true;
      }
    };

    var onPointerUp = function (event) {
      if (!pointerState.active) return;
      pointerState.active = false;
      if (pointerState.moved || event.button !== 0) return;

      if (event.target.closest('[data-if-hero-video-controls], .swiper-pagination, .swiper-pagination-bullet')) {
        return;
      }

      var anchor = event.target.closest('a.if-hero-slide__slide-link, a.if-hero-slide__btn--slide-cta');
      if (anchor) return;

      var frame = event.target.closest('.if-hero-slide__frame--clickable');
      if (!frame) return;

      var slide = frame.closest('.swiper-slide');
      if (slide && !slide.classList.contains('swiper-slide-active')) return;

      var link = frame.querySelector('[data-if-hero-slide-link]');
      if (!link) return;

      event.preventDefault();
      navigateHeroLink(link);
    };

    var onClick = function (event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.target.closest('[data-if-hero-video-controls], .swiper-pagination, .swiper-pagination-bullet')) {
        return;
      }

      var anchor = event.target.closest(
        'a.if-hero-slide__slide-link, a.if-hero-slide__btn--slide-cta'
      );
      if (!anchor || !root.contains(anchor)) return;

      var frame = anchor.closest('.if-hero-slide__frame--clickable');
      if (!frame) return;

      var slide = frame.closest('.swiper-slide');
      if (slide && !slide.classList.contains('swiper-slide-active')) return;

      if (isInvalidHeroHref(anchor.getAttribute('href'))) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      navigateHeroLink(anchor);
    };

    root.addEventListener('pointerdown', onPointerDown, { passive: true });
    root.addEventListener('pointermove', onPointerMove, { passive: true });
    root.addEventListener('pointerup', onPointerUp);
    root.addEventListener('click', onClick, true);

    root._ifHeroClickablePointerDown = onPointerDown;
    root._ifHeroClickablePointerMove = onPointerMove;
    root._ifHeroClickablePointerUp = onPointerUp;
    root._ifHeroClickableClick = onClick;
  }

  function destroyRoot(root) {
    if (!root) return;
    if (root._ifHeroWaitT) {
      clearTimeout(root._ifHeroWaitT);
      root._ifHeroWaitT = null;
    }
    if (root._ifHeroVideoControlsClick) {
      root.removeEventListener('click', root._ifHeroVideoControlsClick);
      root._ifHeroVideoControlsClick = null;
    }
    unbindClickableHeroSlides(root);
    if (root._ifHeroSwiperStackResize) {
      window.removeEventListener('resize', root._ifHeroSwiperStackResize);
      root._ifHeroSwiperStackResize = null;
    }
    if (root._ifHeroStackRO) {
      root._ifHeroStackRO.disconnect();
      root._ifHeroStackRO = null;
    }
    root._ifHeroStackObserveActive = null;
    root._ifHeroStackUseRO = null;
    root.style.removeProperty('--if-hero-stack-footer');
    if (root._ifHeroSwiperInstance) {
      try {
        root._ifHeroSwiperInstance.destroy(true, true);
      } catch (e) {}
      root._ifHeroSwiperInstance = null;
    }
  }

  function initRoot(root, attempt) {
    if (!root) return;
    var el = root.querySelector('.if-hero-swiper__swiper');
    if (!el) return;

    attempt = attempt || 0;
    if (typeof Swiper === 'undefined') {
      if (attempt >= 80) return;
      if (attempt === 0) destroyRoot(root);
      root._ifHeroWaitT = setTimeout(function () {
        root._ifHeroWaitT = null;
        initRoot(root, attempt + 1);
      }, 40);
      return;
    }

    if (root._ifHeroWaitT) {
      clearTimeout(root._ifHeroWaitT);
      root._ifHeroWaitT = null;
    }
    destroyRoot(root);

    var slides = el.querySelectorAll('.swiper-slide').length;
    if (slides === 0) return;

    var autoplay = parseBool(root.dataset.autoplay);
    var delay = parseInt(root.dataset.autoplayDelay || '6000', 10);
    var speed = parseInt(root.dataset.speed || '500', 10);
    var paginationEl = root.querySelector('.if-hero-swiper__pagination');
    var autoHeight = parseBool(root.dataset.autoHeight);
    var stackTextBelow = parseBool(root.dataset.stackTextBelow);
    root._ifHeroStackUseRO = !!(stackTextBelow && typeof ResizeObserver !== 'undefined');

    var hasClickableSlides = !!root.querySelector('.if-hero-slide__slide-link');

    var config = {
      slidesPerView: 1,
      speed: speed,
      effect: 'fade',
      watchSlidesProgress: true,
      autoHeight: autoHeight,
      preventClicks: false,
      preventClicksPropagation: false,
      rewind: slides > 1,
      autoplay:
        autoplay && slides > 1
          ? {
              delay: delay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
      a11y: {
        enabled: true,
      },
      on: {
        init: function (swiper) {
          toggleVideos(root, swiper.slides[swiper.activeIndex]);
          if (!root._ifHeroStackUseRO) {
            scheduleStackMeasure(root, swiper);
          }
        },
        slideChangeTransitionEnd: function (swiper) {
          toggleVideos(root, swiper.slides[swiper.activeIndex]);
          if (root._ifHeroStackUseRO && root._ifHeroStackObserveActive) {
            root._ifHeroStackObserveActive();
          } else {
            scheduleStackMeasure(root, swiper);
          }
        },
      },
    };

    if (slides > 1 && paginationEl) {
      config.pagination = {
        el: paginationEl,
        clickable: true,
      };
    }

    if (hasClickableSlides) {
      config.noSwipingClass = 'swiper-no-swiping';
      config.touchStartPreventDefault = false;
      config.threshold = 12;
    }

    root._ifHeroSwiperInstance = new Swiper(el, config);
    bindClickableHeroSlides(root);

    var onVideoControlsClickBound = function (event) {
      onVideoControlsClick(root, event);
    };
    root.addEventListener('click', onVideoControlsClickBound);
    root._ifHeroVideoControlsClick = onVideoControlsClickBound;

    if (stackTextBelow) {
      var swiper = root._ifHeroSwiperInstance;
      if (root._ifHeroStackUseRO) {
        bindStackResizeObserver(root, swiper);
      }
      var onResize = debounce(function () {
        if (root._ifHeroStackUseRO && root._ifHeroStackObserveActive) {
          root._ifHeroStackObserveActive();
        } else {
          scheduleStackMeasure(root, swiper);
        }
      }, 150);
      window.addEventListener('resize', onResize);
      root._ifHeroSwiperStackResize = onResize;
    }
  }

  function scan(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-if-hero-swiper]').forEach(initRoot);
  }

  function unload(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll('[data-if-hero-swiper]').forEach(destroyRoot);
  }

  if (!window.__ifHeroSwiperListeners) {
    window.__ifHeroSwiperListeners = true;
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
    document.addEventListener('shopify:section:load', function (event) {
      scan(event.target);
    });
    document.addEventListener('shopify:section:unload', function (event) {
      unload(event.target);
    });
  }
})();
